<script lang="ts" setup>
import {
  Dialog,
  Image,
  Picker,
  Popup,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import SidebarNavi from "@/components/mobile/SidebarNavi.vue";
import { useGlobalUser } from "@/stores/globalUser";
import { useLanguages, useStaticUrl } from "@/use/common";
import { ref, watch } from "vue";
import { onBeforeRouteUpdate, useRoute } from "vue-router";
import i18n, { setGalGameLanguage } from "@/locales";
import api from "@/modules/api";
import { useI18n } from "vue-i18n";
import MateSelect from "@/components/mobile/MateSelect.vue";
import { useUI } from "@/stores/ui";

// Use
const user = useGlobalUser();
const { locale } = useI18n({ useScope: "global" });
const uiStore = useUI();
const route = useRoute();

// States
const showLanguageSwitch = ref(false);
const languageValue = ref<number[]>([user.settingDisplayLanguageId]);
const languages = ref(useLanguages());

// Lifecycle
onBeforeRouteUpdate(() => {
  uiStore.showMateSelector = false;
  uiStore.showSideDrawer = false;
});

watch(
  () => route,
  () => {
    uiStore.showMateSelector = false;
    uiStore.showSideDrawer = false;
  },
  { deep: true }
);

// Event

const onLanguagePickerConfirm = async ({ selectedOptions }: any) => {
  const loading = showLoadingToast(i18n.global.t("Message.switchingLanguage"));
  try {
    await user.updateUserApi({
      settingDisplayLanguageId: parseInt(selectedOptions[0].value),
      settingMateLanguageId: parseInt(selectedOptions[0].value),
    });
    setGalGameLanguage(locale.value);
    showLanguageSwitch.value = false;
    loading.close();
    showSuccessToast(i18n.global.t("Message.successfully"));
    location.reload();
  } finally {
    loading.close();
  }
};
</script>
<template>
  <popup
    v-model:show="uiStore.showSideDrawer"
    :style="{ height: '100%' }"
    position="left"
    teleport="body"
    z-index="10000"
    @open="user.refreshUser()"
  >
    <sidebar-navi
      class="h-full"
      @on-show-bottom="
        uiStore.popMateSelector({
          selectionMode: 'selection',
        });
        uiStore.showSideDrawer = false;
      "
      @on-show-bottom-language="
        showLanguageSwitch = true;
        uiStore.showSideDrawer = false;
      "
      @on-close="uiStore.showSideDrawer = false"
      @show-wechat="uiStore.showWechatDialog = true"
    />
  </popup>
  <popup
    v-model:show="showLanguageSwitch"
    position="bottom"
    round
    safe-area-inset-bottom
    teleport="body"
  >
    <Picker
      v-model="languageValue"
      :cancel-button-text="$t('Buttons.cancel')"
      :columns="languages"
      :confirm-button-text="$t('Buttons.confirm')"
      @cancel="showLanguageSwitch = false"
      @confirm="onLanguagePickerConfirm"
    />
  </popup>
  <Dialog
    v-model:show="uiStore.showWechatDialog"
    :confirm-button-text="$t('Buttons.confirm')"
    :title="$t(`SidebarNavi.joinGroupQrcode`)"
    teleport="body"
  >
    <div class="flex flex-col items-center gap-2 py-4">
      <Image
        :src="useStaticUrl('/info/wechat.jpg?x-oss-process=image/format,webp')"
        class="w-[200px] h-[200px]"
        crossorigin="anonymous"
      />
      <p class="px-12 text-sm text-center">
        {{ $t(`SidebarNavi.joinGroupTip`) }}
      </p>
    </div>
  </Dialog>
  <popup
    v-model:show="uiStore.showMateSelector"
    class="max-h-[90%] overflow-hidden grid grid-rows-[1fr]"
    closeable
    position="bottom"
    round
    safe-area-inset-bottom
    teleport="body"
  >
    <mate-select
      :mate-only="uiStore.mateSelectorParam.mateOnly"
      :selection-mode="uiStore.mateSelectorParam.selectionMode"
      :exclude="uiStore.mateSelectorParam.exclude"
      :switch-mate="uiStore.mateSelectorParam.switchMate"
      :callback="uiStore.mateSelectorParam.callback"
      :title="uiStore.mateSelectorParam.title"
      :show-unpublished="uiStore.mateSelectorParam.showUnpublished"
      :on-click="uiStore.mateSelectorParam.onClick"
      :include-self-avatar="uiStore.mateSelectorParam.includeSelfAvatar"
      :selected-mate-id="uiStore.mateSelectorParam.selectedMateId"
    />
  </popup>
  <ThemeSwitcher />
</template>
